/**
 * ★ Fase 58 — LANGUAGE GATE sulla root "/".
 * Il dominio (es. greenparadisehcf.info) atterra qui: l'utente sceglie
 * SUBITO la lingua e viene instradato al funnel giusto:
 *   • Italiano → /f/it  (il NUOVO funnel IT multi-paese — il vecchio non esiste più)
 *   • English  → /en    (landing EN esistente)
 *   • Español  → /es    (landing ES esistente)
 * La scelta viene persistita (i18n + cookie) come fa LangGuard.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import i18n from '../../i18n';
import { ArrowRight } from 'lucide-react';
import { setPageMeta, ogImageFor } from '../../lib/seo';

const INK = '#081712';
const EMERALD = '#0C2B21';
const GOLD = '#D6B25E';
const IVORY = '#F4EEE1';
const SAND = '#CFC6B2';

const LANGS = [
  { code: 'it', flag: '🇮🇹', label: 'Italiano', hint: 'Continua in italiano', to: '/f/it' },
  { code: 'en', flag: '🇬🇧', label: 'English', hint: 'Continue in English', to: '/f/en' },
  { code: 'es', flag: '🇪🇸', label: 'Español', hint: 'Continúa en español', to: '/f/es' },
];

export default function LanguageGate() {
  const navigate = useNavigate();

  useEffect(() => {
    // ★ Fase 89 — meta social della root (thumbnail brand, og:url = "/")
    setPageMeta({
      title: 'HCF Property — Select your language',
      description: 'HCF Property — Dal mattone al patrimonio · Del ladrillo al patrimonio · From bricks to wealth. Verification Pass Albania, Indonesia, Thailand.',
      image: ogImageFor('default'),
      imageAlt: 'HCF Property — Dal mattone al patrimonio. Verification Pass Albania.',
      path: '/',
    });
    window.scrollTo(0, 0);
  }, []);

  const choose = (lang) => {
    try {
      if (i18n.language !== lang.code) i18n.changeLanguage(lang.code);
      localStorage.setItem('gp_locale', lang.code);
      document.cookie = `gp_locale=${lang.code}; path=/; max-age=${365 * 86400}; SameSite=Lax`;
      document.cookie = `gp_lang_chosen=${lang.code}; path=/; max-age=${365 * 86400}; SameSite=Lax`;
    } catch { /* best-effort */ }
    navigate(lang.to);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-5 font-serif relative"
      style={{ background: INK, color: IVORY }}
      data-testid="language-gate"
    >
      {/* alone dorato soft */}
      <div
        className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: `radial-gradient(760px 480px at 50% 30%, ${GOLD}17, transparent 70%)` }}
      />

      <div className="relative w-full max-w-md text-center">
        <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-14 w-auto mx-auto" />
        <h1 className="mt-7 text-3xl md:text-4xl leading-tight" data-testid="language-gate-title">
          Benvenuto · Welcome · Bienvenido
        </h1>
        <p className="mt-3 text-sm font-sans" style={{ color: SAND }}>
          Scegli la tua lingua per continuare
        </p>

        <div className="mt-8 space-y-3">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => choose(l)}
              className="group w-full rounded-2xl border px-5 py-4 flex items-center gap-4 text-left transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2"
              style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B8`, backdropFilter: 'blur(12px)' }}
              data-testid={`language-gate-${l.code}`}
            >
              <span className="text-2xl" aria-hidden="true">{l.flag}</span>
              <span className="flex-1">
                <span className="block text-lg" style={{ color: IVORY }}>{l.label}</span>
                <span className="block text-xs font-sans" style={{ color: SAND }}>{l.hint}</span>
              </span>
              <ArrowRight
                className="w-5 h-5 transition-transform duration-150 group-hover:translate-x-1"
                style={{ color: GOLD }}
              />
            </button>
          ))}
        </div>

        <p className="mt-10 text-[11px] font-sans tracking-[0.25em] uppercase" style={{ color: `${SAND}99` }}>
          © {new Date().getFullYear()} HCF Property
        </p>
      </div>
    </div>
  );
}
